/**
 * Toolbar + visualização da contestada com 2 modos (E29.F2/ADR-0040). // ux-neutrality:allow doc-comment
 *
 * Modos: // ux-neutrality:allow doc-comment
 *  - compare: lado-a-lado padrão selecionado + contestada (+ marcação por // ux-neutrality:allow doc-comment
 *    critério sobre a contestada, E23.F10/ADR-0027; régua, marcadores e // ux-neutrality:allow doc-comment
 *    anotações manuais E36.F3 vivem aqui, sobre <img> — ver CompareView) // ux-neutrality:allow doc-comment
 *  - overlap: mesa "Sobrepor & alinhar" — canvas Fabric com a contestada // ux-neutrality:allow doc-comment
 *    manipulável (arrastar/escalar/rotacionar) sobre o padrão, opacidade real // ux-neutrality:allow doc-comment
 *    e Resetar (OverlapCanvas). Substitui os antigos modos Sobrepor + Alinhar. // ux-neutrality:allow doc-comment
 *
 * E37.F2: toolbar redesenhada em grupos semânticos com ícones + tooltip + // ux-neutrality:allow doc-comment
 * overflow responsivo; feedback de captura e de salvamento migrados para Toast. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Blueprint } from '@/components/ui/Blueprint';
import { useToast } from '@/components/ui/Toast';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import type { CriterionBandStatus, QuestionedLocalizationsData } from '@/lib/case_workspace';
import { isTextEntryTarget } from '@/lib/keyboard_guards';
import type { MesaAnnotationData } from '@/lib/mesa_annotations';
import { captureMesaView } from '@/lib/mesa_capture';
import { AnnotationLayer } from './AnnotationLayer';
import { TriagemAnnotationLayer } from './TriagemAnnotationLayer';
import { useAnnotationUi } from './AnnotationTools';
import { ContestedToolbar } from './ContestedToolbar';
import {
  GridOverlay,
  INSPECTION_FILTERS_OFF,
  type InspectionFiltersState,
} from './ContestedInspectionFilters';
import { OverlapCanvas, OVERLAP_IDENTITY, type OverlapTransform } from './OverlapCanvas';
import { useMesaAnnotations } from './useMesaAnnotations';
import { useSignatureBlob } from './useSignatureBlob';
import { CompareView } from './CompareView';
import { type ZoomCommand } from './ZoomPanViewport';

type Mode = 'compare' | 'overlap';

interface LaudoCaptureProp {
  busy: boolean;
  onCapture: (label: string, image: Blob) => Promise<boolean>;
}

/** Captura da vista corrente e dispara toast de feedback (T03/E37.F2). */
function useLaudoCaptureUi(
  laudoCapture: LaudoCaptureProp | undefined,
  viewportRef: React.RefObject<HTMLDivElement>,
  label: () => string,
  toast: ReturnType<typeof useToast>['toast'],
): { trigger: (() => void) | null } {
  if (laudoCapture === undefined) return { trigger: null };

  async function run(capture: LaudoCaptureProp): Promise<void> {
    const root = viewportRef.current;
    if (root === null) return;
    try {
      const image = await captureMesaView(root);
      const ok = await capture.onCapture(label(), image);
      if (ok) {
        toast({
          title: ptBR.workspaceCaso.captureSuccess,
          variant: 'success',
          testId: 'capture-success',
        });
      } else {
        toast({
          title: ptBR.workspaceCaso.captureFailure,
          variant: 'danger',
          testId: 'capture-error',
        });
      }
    } catch {
      toast({
        title: ptBR.workspaceCaso.captureFailure,
        variant: 'danger',
        testId: 'capture-error',
      });
    }
  }

  return { trigger: () => void run(laudoCapture) };
}

export interface ContestedCanvasToolsProps {
  caseId: string;
  /** E36.F3: análise dona das anotações manuais persistidas. */
  analysisId: string;
  contested: SignatureRecordData;
  selectedPattern: SignatureRecordData | null;
  // E23.F10/ADR-0027: marcação por critério sobre a contestada (null em
  // análises legadas/não-completed). E25.F3/ADR-0035: a cor vem do status
  // resolvido pelo backend (`criteriaStatus`), não de LR client-side.
  localizations?: QuestionedLocalizationsData | null;
  criteriaStatus?: Record<string, CriterionBandStatus> | null;
  /** E36.F4: persiste a vista capturada como imagem do laudo (hook na página,
   * compartilhado com o checklist "Conteúdo do laudo"). Ausente = sem botão. */
  laudoCapture?: LaudoCaptureProp;
  /** E37.F3.T03: modo foco — recolhe as colunas laterais (estado na página). */
  focusMode?: boolean;
  onToggleFocus?: (() => void) | null;
  // E38.F2: visibilidade das marcações do sistema PERSISTIDA por análise
  // (`show_system_markers`, default oculto). Quando `onToggleSystemMarkers` é
  // passado, o toggle é controlado (a página persiste e o laudo respeita); sem
  // ele, cai num estado local efêmero (testes/usos legados — comportamento antigo).
  showSystemMarkers?: boolean;
  onToggleSystemMarkers?: (() => void) | undefined;
}

function MarkersLegend(): JSX.Element {
  return (
    <div className="markers-legend" data-testid="markers-legend">
      <span className="markers-legend-item markers-legend-item--convergent">
        {ptBR.workspaceCaso.markersLegendConvergent}
      </span>
      <span className="markers-legend-item markers-legend-item--neutral">
        {ptBR.workspaceCaso.markersLegendNeutral}
      </span>
      <span className="markers-legend-item markers-legend-item--divergent">
        {ptBR.workspaceCaso.markersLegendDivergent}
      </span>
      <p className="markers-disclaimer">{ptBR.workspaceCaso.markersDisclaimer}</p>
    </div>
  );
}

interface ToolsBodyProps {
  mode: Mode;
  invert: boolean;
  grid: boolean;
  patternSrc: string | null;
  contestedSrc: string | null;
  markersOn: boolean;
  localizations: QuestionedLocalizationsData | null;
  criteriaStatus: Record<string, CriterionBandStatus> | null;
  rulerOn: boolean;
  rulerMeasured: boolean;
  onRulerMeasured: () => void;
  zoomCommand: ZoomCommand | null;
  /** E37.F1.T03: espelhar zoom/pan entre os painéis K e Q do Comparar. */
  syncPanels: boolean;
  overlapTransform: OverlapTransform;
  onOverlapTransform: (next: OverlapTransform) => void;
  drawActive: boolean;
  annotationsLayer: React.ReactNode;
  /** E36.F3.T03: anotações persistidas — a mesa as exibe ancoradas à contestada. */
  annotations: MesaAnnotationData[];
  /** E36.F4: alvo da captura — o viewport com a vista corrente. */
  viewportRef: React.RefObject<HTMLDivElement>;
}

/** Corpo abaixo da toolbar: legenda, disclaimer da régua e o viewport com o
 * modo corrente. Extraído do componente principal por complexidade (eslint). */
function ToolsBody(props: ToolsBodyProps): JSX.Element {
  return (
    <>
      {props.markersOn && <MarkersLegend />}
      {props.rulerOn && (
        <p className="ruler-disclaimer" data-testid="ruler-disclaimer">
          {!props.rulerMeasured && (
            <strong data-testid="ruler-hint">{ptBR.workspaceCaso.inspectRulerHint} </strong>
          )}
          {ptBR.workspaceCaso.inspectRulerNote}
        </p>
      )}

      <div
        ref={props.viewportRef}
        // gotcha: prettier-plugin-tailwindcss come o espaço de sufixos
        // condicionais DENTRO do template de className — compor fora do JSX.
        className={
          props.invert ? 'contested-viewport contested-viewport--inverted' : 'contested-viewport'
        }
        data-testid={`contested-mode-${props.mode}`}
      >
        {props.mode === 'overlap' ? (
          <OverlapCanvas
            patternSrc={props.patternSrc}
            contestedSrc={props.contestedSrc}
            transform={props.overlapTransform}
            onTransformChange={props.onOverlapTransform}
            viewCommand={props.zoomCommand}
            annotations={props.annotations}
          />
        ) : (
          <CompareView
            patternSrc={props.patternSrc}
            contestedSrc={props.contestedSrc}
            showMarkers={props.markersOn}
            localizations={props.localizations}
            criteriaStatus={props.criteriaStatus}
            rulerActive={props.rulerOn}
            onRulerMeasured={props.onRulerMeasured}
            zoomCommand={props.zoomCommand}
            syncPanels={props.syncPanels}
            contestedPanDisabled={props.drawActive}
            annotationsLayer={props.annotationsLayer}
          />
        )}
        {props.grid && <GridOverlay />}
      </div>
    </>
  );
}

/**
 * E36.F1/E38.F2: visibilidade das "marcações do sistema". Quando a página passa
 * `onToggleSystemMarkers`, o toggle é CONTROLADO por `showSystemMarkers` (estado
 * persistido por análise, o laudo respeita); sem ele, cai num estado local
 * efêmero (comportamento antigo — testes/usos legados). Extraído para fora do
 * componente para não inflar sua complexidade ciclomática (eslint).
 */
function useSystemMarkersControl(
  showSystemMarkers: boolean | undefined,
  onToggleSystemMarkers: (() => void) | undefined,
): { showMarkers: boolean; toggleMarkers: () => void } {
  const [localShow, setLocalShow] = useState(false);
  if (onToggleSystemMarkers !== undefined) {
    return { showMarkers: showSystemMarkers ?? false, toggleMarkers: onToggleSystemMarkers };
  }
  return {
    showMarkers: localShow,
    toggleMarkers: (): void => setLocalShow((current) => !current),
  };
}

export function ContestedCanvasTools({
  caseId,
  analysisId,
  contested,
  selectedPattern,
  localizations = null,
  criteriaStatus = null,
  laudoCapture,
  focusMode = false,
  onToggleFocus = null,
  showSystemMarkers,
  onToggleSystemMarkers,
}: ContestedCanvasToolsProps): JSX.Element {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>('compare');
  // E29.F2/ADR-0040 §A: transform da mesa "Sobrepor & alinhar" vive AQUI —
  // persiste ao alternar Comparar↔Mesa (corrige "alinho, sobreponho e volta").
  const [overlapTransform, setOverlapTransform] = useState<OverlapTransform>(OVERLAP_IDENTITY);
  // E36.F1/E38.F2: visibilidade das marcações do sistema (ver useSystemMarkersControl).
  const { showMarkers, toggleMarkers } = useSystemMarkersControl(
    showSystemMarkers,
    onToggleSystemMarkers,
  );
  // E26.F4/ADR-0039: filtros de inspeção — estado efêmero, apresentação-only.
  const [filters, setFilters] = useState<InspectionFiltersState>(INSPECTION_FILTERS_OFF);
  // E27.F1: affordance da régua — hint do gesto até a primeira medição.
  const [rulerMeasured, setRulerMeasured] = useState(false);
  // E36.F2: comando de zoom da toolbar (E37.F1: + presets, in/out e atalhos) —
  // tick força reaplicação mesmo repetindo a ação; consumido pelos viewports
  // (Comparar) e pela mesa.
  const [zoomCommand, setZoomCommand] = useState<ZoomCommand | null>(null);
  // E37.F1.T03: sincronização K↔Q no Comparar — default LIGADO (decisão owner
  // 2026-06-11); efêmero, apresentação-only.
  const [syncPanels, setSyncPanels] = useState(true);
  // E36.F3: anotações manuais persistidas (setas/círculos) + estado das
  // ferramentas de desenho.
  const mesaAnnotations = useMesaAnnotations(caseId, analysisId);
  const annotationUi = useAnnotationUi(mesaAnnotations.remove);
  // E47.F3: as duas camadas vivem separadas de propósito. As do PERITO são
  // capturáveis para o laudo; as sugestões da análise preliminar não são, até
  // ele assumi-las (ADR-0058 §2.5.bis). Por isso as sugestões também ficam fora
  // do canvas Fabric do Sobrepor — o lower-canvas é rasterizado na captura, e
  // ali não há como excluí-las.
  const anotacoesDoPerito = mesaAnnotations.annotations.filter((a) => a.origem === 'perito');
  const sugestoesDaTriagem = mesaAnnotations.annotations.filter((a) => a.origem === 'triagem');
  const selecionadaEhSugestao = sugestoesDaTriagem.some((a) => a.id === annotationUi.selectedId);
  // E36.F4: captura da vista corrente → imagem do laudo.
  const viewportRef = useRef<HTMLDivElement>(null);
  const captureUi = useLaudoCaptureUi(
    laudoCapture,
    viewportRef,
    () => {
      const view =
        mode === 'overlap' ? ptBR.workspaceCaso.toolOverlap : ptBR.workspaceCaso.toolCompare;
      return filters.invert ? `${view} — ${ptBR.workspaceCaso.captureInvertedSuffix}` : view;
    },
    toast,
  );

  // E37.F2/T03: erros de salvamento de anotação → toast.
  const prevSaveError = useRef(false);
  useEffect(() => {
    if (mesaAnnotations.saveError && !prevSaveError.current) {
      toast({
        title: ptBR.workspaceCaso.annotateSaveError,
        variant: 'danger',
        testId: 'annotate-save-error',
      });
    }
    prevSaveError.current = mesaAnnotations.saveError;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesaAnnotations.saveError]);

  const dispatchZoom = useCallback((action: ZoomCommand['action'], percent?: number): void => {
    setZoomCommand((current) => ({ action, percent, tick: (current?.tick ?? 0) + 1 }));
  }, []);

  // E37.F1.T02: atalhos de teclado da mesa — +/− zoom, 0 caber, 1 = 100%.
  // Guards: digitação em campos de texto e combinações com modificador (o
  // Ctrl/Cmd± do navegador continua dele) nunca são interceptadas.
  useEffect(() => {
    const actions: Record<string, ZoomCommand['action']> = {
      '+': 'in',
      '=': 'in',
      '-': 'out',
      _: 'out',
      '0': 'fit',
      '1': 'hundred',
    };
    function onKeyDown(event: KeyboardEvent): void {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;
      const action = actions[event.key];
      if (action === undefined) return;
      event.preventDefault();
      dispatchZoom(action);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatchZoom]);

  const contestedSrc = useSignatureBlob(caseId, contested.id);
  const patternSrc = useSignatureBlob(caseId, selectedPattern?.id);

  const hasMarkers =
    localizations !== null && localizations.criteria.some((c) => c.markers.length > 0);
  const markersInCompare = hasMarkers && mode === 'compare';
  const markersOn = markersInCompare && showMarkers;
  // Régua só no modo Comparar (ADR-0039 §D: medição DENTRO de uma imagem;
  // nos modos sobrepostos a medida seria ambígua entre as duas capturas).
  const rulerOn = filters.ruler && mode === 'compare';
  const drawActive = annotationUi.drawTool !== null && mode === 'compare';

  // E36.F3: um modo de gesto por vez — desenho e régua são exclusivos.
  const annotationToolbarUi = {
    ...annotationUi,
    setTool: (tool: Parameters<typeof annotationUi.setTool>[0]): void => {
      annotationUi.setTool(tool);
      setFilters((current) => ({ ...current, ruler: false }));
    },
  };

  function onToggleFilter(key: keyof InspectionFiltersState): void {
    if (key === 'ruler' && !filters.ruler) annotationUi.setTool(null);
    setFilters((current) => ({ ...current, [key]: !current[key] }));
  }

  function onModeChange(next: Mode): void {
    // Trocar de modo encerra o gesto corrente (desenho fica do Comparar; a
    // mesa tem os próprios gestos — E36.F3.T03 estende o render para lá).
    annotationUi.setTool(null);
    annotationUi.select(null);
    setMode(next);
  }

  return (
    <Blueprint
      as="section"
      className="contested-canvas"
      aria-label={ptBR.workspaceCaso.contestedTitle}
    >
      <ContestedToolbar
        mode={mode}
        onMode={onModeChange}
        filters={filters}
        onToggleFilter={onToggleFilter}
        rulerAvailable={mode === 'compare'}
        annotationUi={mode === 'compare' ? annotationToolbarUi : null}
        selectedIsTriagem={selecionadaEhSugestao}
        onAdotar={mesaAnnotations.adotar}
        showMarkersButton={markersInCompare}
        showMarkers={showMarkers}
        onToggleMarkers={toggleMarkers}
        onZoomCommand={dispatchZoom}
        syncPanels={syncPanels}
        onToggleSync={() => setSyncPanels((current) => !current)}
        onCapture={captureUi.trigger}
        captureBusy={laudoCapture?.busy ?? false}
        focusMode={focusMode}
        onToggleFocus={onToggleFocus}
      />

      <ToolsBody
        mode={mode}
        invert={filters.invert}
        grid={filters.grid}
        patternSrc={patternSrc}
        contestedSrc={contestedSrc}
        markersOn={markersOn}
        localizations={localizations}
        criteriaStatus={criteriaStatus}
        rulerOn={rulerOn}
        rulerMeasured={rulerMeasured}
        onRulerMeasured={() => setRulerMeasured(true)}
        zoomCommand={zoomCommand}
        syncPanels={syncPanels}
        overlapTransform={overlapTransform}
        onOverlapTransform={setOverlapTransform}
        drawActive={drawActive}
        annotations={anotacoesDoPerito}
        viewportRef={viewportRef}
        annotationsLayer={
          <>
            <AnnotationLayer
              annotations={anotacoesDoPerito}
              drawTool={drawActive ? annotationUi.drawTool : null}
              drawColor={annotationUi.drawColor}
              selectedId={annotationUi.selectedId}
              onSelect={annotationUi.select}
              onDraw={mesaAnnotations.draw}
              onMove={mesaAnnotations.move}
              shapesInert={rulerOn}
            />
            <TriagemAnnotationLayer
              annotations={sugestoesDaTriagem}
              selectedId={annotationUi.selectedId}
              onSelect={annotationUi.select}
              inert={drawActive || rulerOn}
            />
          </>
        }
      />
    </Blueprint>
  );
}
